<template>
  <v-row class="ma-0 h-100">
    <split-view>
      <template #left>
        <div class="px-1 pt-2 pb-1 d-flex items-center justify-space-between">
          <span>Files <v-icon>mdi-cloud-check-outline</v-icon></span>
          <v-menu location="bottom" :offset="0">
            <template #activator="{ props }">
              <v-btn
                class="show-on-hover-item"
                v-bind="props"
                size="small"
                variant="plain"
                tabindex="-1"
                icon="mdi-dots-vertical"
                :style="{ 'cursor': 'pointer' }"
              />
            </template>
            <template v-slot:default>
              <v-list density="compact" class="pt-0">
                <v-list-item
                  v-for="item in projectMenuItems"
                  :key="item.label"
                  class="py-0 min-h-0"
                  @click="item.action"
                >
                  <v-list-item-title class="text-sm d-flex align-center ga-1">
                    <v-icon size="16" class="pa-0">{{ item.icon }}</v-icon>
                    <span>{{ item.label }}</span>
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </template>
          </v-menu>
        </div>
        <v-divider />
        <v-list
          height="774"
          bg-color="transparent"
          density="compact"
          open-strategy="multiple"
          v-model:selected="tabs"
          v-model:opened="opened"
        >
          <code-file-list-item
            v-for="item in flattenedItems"
            :key="item.path"
            :item="item"
            :fileObjects="fileObjects"
            @click="toggle(item)"
          />
        </v-list>
      </template>
      <template #right>
        <template v-for="(object, filename) in fileObjects" :key="filename">
          <div v-if="'root/' + openedTab === filename" style="position: relative;">
            <workflow-aggregates-code-viewer
              :object="object"
              v-model:contents="object.contents"
              v-model:draftContents="object.draftContents"
              @saveFile="saveFile"
              @deleteFile="deleteFile"
              @update:object="handleObjectUpdate"
            />
          </div>
        </template>
      </template>
    </split-view>
    <PreviewPromptDialog
      v-model="openPreviewPrompt"
      :content="previewPromptContent"
      @update:modelValue="onTogglePreviewDialog"
    />
  </v-row>
</template>

<script setup>
import { ref, computed, watch, provide } from 'vue'
import SplitView from '@/components/SplitView.vue'
import CodeFileListItem from '@/components/CodeFileListItem.vue'
import PreviewPromptDialog from '@/components/PreviewPromptDialog.vue'
import WorkflowAggregatesCodeViewer from '@/components/WorkflowAggregatesCodeViewer.vue'
import {
  CODE_EDITOR_APPLY_PATCH_FILE,
  CODE_EDITOR_APPLY_PATCH_FOLDER,
  CODE_EDITOR_OPEN_FILE,
  CODE_EDITOR_SAVE_FILE,
  CODE_EDITOR_DOWNLOAD_FILE,
  CODE_EDITOR_DOWNLOAD_FOLDER,
  CODE_EDITOR_DELETE_FILE,
  CODE_EDITOR_DELETE_FOLDER,
  CODE_EDITOR_COPY_PROMPT
} from '@/codegen/constants'
import { flattenTree } from '@/codegen/utils'
import { downloadFile } from '@/utils/fsUtils'
import JSZip from 'jszip'
import _ from 'lodash'

const {
  fileTree,
  fileObjects,
  saveFile,
  deleteFile,
  getDomainItemFinalPrompt
} = defineProps({
  fileTree: Object,
  fileObjects: Object,
  saveFile: Function,
  deleteFile: Function,
  getDomainItemFinalPrompt: Function
})

const emit = defineEmits(['update:fileTree', 'update:fileObjects'])

const projectMenuItems = [
  { label: 'Download', icon: 'mdi-download', action: () => downloadZip(fileTree.root, 'root/') }
]

// // File-Related Operations
// const newFile = () => {
//   console.log('New File')
// }

const tabs = ref([])
const opened = ref(['root', 'root/src'])
const openedTab = ref('')
const dirtyFolders = ref(new Set())
const openPreviewPrompt = ref(false)
const previewPromptContent = ref('')

const flattenedItems = computed(() => flattenTree(fileTree.root || {}, opened.value, '', 0))

watch(tabs, (val) => {
  if (val[0]) {
    openedTab.value = val[0]
  } else {
    tabs.value[0] = openedTab.value
  }
})

watch(() => fileObjects, (newFileObjects) => {
  const set = new Set()
  Object.entries(newFileObjects).forEach(([filePath, fileObject]) => {
    if (fileObject.draftContents) {
      const parts = fileObject.filepath.split('/')
      let path = ''

      for (let i = 0; i < parts.length; i++) {
        path = i === 0 ? parts[i] : `${path}/${parts[i]}`
        set.add(path)
      }
    }
  })
  dirtyFolders.value = set
}, { immediate: true, deep: true })

function handleObjectUpdate (object) {
  emit('update:object', object)
}

provide('treeAction', (data) => {
  // Centralized handling logic
  switch (data.type) {
    case CODE_EDITOR_OPEN_FILE:
      openFile(fileObjects['root/' + data.path])
      break
    case CODE_EDITOR_SAVE_FILE:
      saveFile(fileObjects['root/' + data.path])
      break
    case CODE_EDITOR_APPLY_PATCH_FILE:
      applyPatchFile(data.path)
      break
    case CODE_EDITOR_APPLY_PATCH_FOLDER:
      applyPatchFolder('root/' + data.path)
      break
    case CODE_EDITOR_DOWNLOAD_FILE:
      downloadFile(data.name, 'application/octet-stream', fileObjects['root/' + data.path]?.contents || '')
      break
    case CODE_EDITOR_DOWNLOAD_FOLDER:
      downloadFolder(data.path)
      break
    case CODE_EDITOR_DELETE_FOLDER:
      deleteFolder('root/' + data.path)
      break
    case CODE_EDITOR_DELETE_FILE:
      deleteFile(fileObjects['root/' + data.path])
      break
    case CODE_EDITOR_COPY_PROMPT:
      const promptPath = `root/${data.path}`
      const prompt = getDomainItemFinalPrompt(fileObjects[promptPath])
      previewPromptContent.value = prompt
      openPreviewPrompt.value = true
      break
    default:
      console.warn('Unknown treeAction occurred')
      console.log(data)
      break
  }
})

const toggle = (item) => {
  const index = opened.value.indexOf(item.path)
  if (index >= 0) {
    opened.value.splice(index, 1)
  } else {
    opened.value.push(item.path)
  }
}

const openFile = (file) => {
  const path = file.filepath.replace('root/', '')
  const index = opened.value.indexOf(path)
  if (index === -1) {
    opened.value.push(path)
  }
  openedTab.value = path
}

const applyPatchFile = (path) => {
  const file = fileObjects[path]
  if (file.contents !== file.draftContents) {
    const tempFile = _.cloneDeep(file)
    tempFile.contents = tempFile.draftContents
    tempFile.draftContents = null
    saveFile(tempFile)
  }
}

const applyPatchFolder = (path) => {
  const files = Object.entries(fileObjects)
    .filter(([key, value]) => key.startsWith(path))
    .map(([key, value]) => value)

  for (const file of files) {
    if (file.draftContents !== null && file.contents !== file.draftContents) {
      const tempFile = _.cloneDeep(file)
      tempFile.contents = tempFile.draftContents
      tempFile.draftContents = null
      saveFile(tempFile)
    }
  }
}

const downloadFolder = (path) => {
  const folders = path.split('/').slice(0, -1)
  let tree = fileTree
  for (const folder of folders) {
    tree = tree[folder]
  }
  downloadZip(tree, path)
}

const deleteFolder = (path) => {
  const files = Object.entries(fileObjects)
    .filter(([key, value]) => key.startsWith(path))
    .map(([key, value]) => value)

  for (const file of files) {
    deleteFile(file)
  }

  const parts = path.split('/')
  const newTree = _.cloneDeep(fileTree)
  let parent = newTree
  for (let i = 0; i < parts.length - 1; i++) {
    parent = parent[parts[i]]
    if (!parent) return // path doesn't exist
  }

  const lastPart = parts[parts.length - 1]
  if (parent[lastPart] && typeof parent[lastPart] === 'object') {
    delete parent[lastPart]
  }

  emit('update:fileTree', newTree)
}

const downloadZip = (fileTree, path, zipName = 'project.zip') => {
  const result = new JSZip()

  const addFilesToZip = (zip, fileTree, path) => {
    Object.keys(fileTree).forEach(name => {
      if (Object.keys(fileTree[name]).length) {
        const folder = zip.folder(name)
        addFilesToZip(folder, fileTree[name], path + name + '/')
      } else {
        zip.file(name, fileObjects[path + name].contents)
      }
    })
  }

  Object.keys(fileTree).forEach(key => {
    const folder = result.folder(key)
    addFilesToZip(folder, fileTree[key], path + key + '/')
  })

  result.generateAsync({ type: 'blob' }).then(content => {
    const link = document.createElement('a')
    link.href = URL.createObjectURL(content)
    link.download = zipName
    link.click()
  })
}

const onTogglePreviewDialog = (val) => {
  if (!val) {
    previewPromptContent.value = ''
  }
}
</script>

<style scoped>
/* Custom styling for v-list inside v-menu overlay */
.v-menu > .v-overlay__content > .v-list {
  padding: 4px !important;
}

.v-menu > .v-overlay__content > .v-list > .v-list-item {
  background-color: var(--light-2);
  cursor: pointer;
  min-height: 28px;
}

.v-menu > .v-overlay__content > .v-list {
  padding: 4px !important;
  border-radius: 4px !important;
}

.v-list-item--density-compact:not(.v-list-item--nav).v-list-item--one-line {
  padding-inline: 4px !important;
}

.v-list-item--density-compact:not(.v-list-item--nav).v-list-item--one-line .file-tree-item {
  padding-inline: 16px !important;
}
</style>
